/**
 * @fileoverview Table row component for companies list display
 * @active CONFIRMED - This component is actively used in the application
 * 
 * This component renders a single row in the companies table, displaying various
 * company properties including accelerator information, tags, metrics, and status.
 * It is part of the active implementation of the companies list table.
 * 
 * @see {@link src/components/companies/companiesList/newCompaniesList/companiesTable/companyInfoTable/index.tsx} - Parent table component
 * @see {@link src/assets/svg/accelerators/acceleratorSelector.tsx} - Accelerator icon selector function
 * @see {@link src/components/companies/companiesList/newCompaniesList/companiesTable/shared/TdWrapper.tsx} - Table cell wrapper
 * @see {@link src/components/companies/companiesList/newCompaniesList/companiesTable/shared/RowText.tsx} - Text display component
 * @see {@link src/components/companies/companiesList/companiesListComponents/tableMain/TableElements.jsx} - Legacy implementation (deprecated)
 */

"use client";

import { useContext, memo, useEffect } from "react";

import { Flex, Tr, Text, Tag } from "@chakra-ui/react";
import { useSelector } from "react-redux";

import { type TableContextType } from "@/components/companies/companiesList/newCompaniesList/types";
import { ExternalUrlIcons } from "@/components/ui/buttons/ExternalUrlIcons.server";
import { useActions } from "@/hooks/useActions";
import useCompaniesListRequest from "@/hooks/useCompaniesListRequest";
import newViewSelectors from "@/store/newView/newView.selectors";

import TableContext from "../context/TableContext";
import AcceleratorListView from "../shared/AcceleratorListView";
import ColoredTags from "../shared/coloredTags";
import RowText from "../shared/RowText";
import TdWrapper from "../shared/TdWrapper";

import CompanyNameRow from "./CompanyNameRow";

interface CompanyItem {
  id: string;
  company: {
    companyName: string;
    fileLogo?: string;
    slug: string;
  };
  description?: string;
  links?: {
    urlWebsite?: string;
  };
  name?: string;
  status?: string;
  yearFounded?: string;
  tags?: Array<{ name: string; color: string }>;
  accelerator?: Array<{ id: string; slug: string }> | { id: string; slug: string };
  [key: string]: any;
}

type CompaniesInfoTableRowProps = {
  item: CompanyItem;
  idx?: number;
}

interface ViewParams {
  columnsWidth?: number[];
  orderBy?: { value: string };
  direction?: { value: string };
  limit?: number;
  [key: string]: any;
}

interface ViewState {
  id: string;
  [key: string]: any;
}

interface ArrayFilters {
  [key: string]: string[];
}

const statusColors = {
  Active: { bg: "#f0f0f022", color: "#666666" },
  Public: { bg: "#e8eaf622", color: "#3f51b5" },
  Acquired: { bg: "#c5e1a522", color: "#2e7d32" },
  Inactive: { bg: "#b5580722", color: "#b55807" },
  Closed: { bg: "#ef9a9a22", color: "#c62828" },
} as const;

const CompaniesInfoTableRow = memo(function CompaniesInfoTableRow({ item, idx }: CompaniesInfoTableRowProps) {
  const { setViewParams, setArrayFilters } = useActions();
  const currentNewView = useSelector(newViewSelectors.getCurrentView()) as ViewState;
  const viewParams = useSelector(newViewSelectors.getViewParams()) as Record<string, ViewParams>;
  const arrayFilters = useSelector(newViewSelectors.getArrayFilters()) as Record<string, ArrayFilters>;
  const currentParams = viewParams?.[currentNewView?.id];
  const currentArrayFilters = arrayFilters?.[currentNewView?.id];
  
  const { columnsWidth, selectedRows } = useContext<TableContextType>(TableContext);
  const isSelected = selectedRows.some(row => row.id === item.id);
  const { getCompanies } = useCompaniesListRequest();

  // Debug logs for component rendering and data - AFTER context initialization
  console.log('CompaniesInfoTableRow - Rendering:', {
    itemId: item?.id,
    hasAccelerator: Boolean(item?.accelerator),
    acceleratorData: item?.accelerator,
    columnsWidth: columnsWidth,
    selectedRows: selectedRows?.length,
    currentViewId: currentNewView?.id,
    viewParams: currentParams
  });

  // Debug styling application
  useEffect(() => {
    const row = document.querySelector(`tr[data-row-id="${item?.id}"]`);
    if (row) {
      console.log('CompaniesInfoTableRow - Styles:', {
        rowId: item?.id,
        rowElement: row,
        computedStyles: window.getComputedStyle(row),
        tdElements: Array.from(row.querySelectorAll('td')).map(td => ({
          id: td.getAttribute('id'),
          borderBottom: window.getComputedStyle(td).borderBottom,
          backgroundColor: window.getComputedStyle(td).backgroundColor
        }))
      });
    }
  }, [item?.id]);

  const onFilteredByTag = ({ key, item }: { key: string; item: CompanyItem }) => {
    // Create a safe copy of filters with proper null checks
    const filters = {
      ...(currentArrayFilters || {}),
      [key]: [...((currentArrayFilters && currentArrayFilters[key]) || []), item?.slug || ""],
    };

    const options = {
      orderBy: currentParams?.orderBy?.value || undefined,
      direction: currentParams?.direction?.value?.toUpperCase() || undefined,
      arrayFilters: filters,
      limit: currentParams?.limit || 25,
      page: 1,
    };
    if (currentNewView?.id) {
      setArrayFilters({
        key: currentNewView.id,
        filters,
      });
      setViewParams({
        key: currentNewView.id,
        params: {
          arrayFilters: filters,
        },
      });
    }
    if (getCompanies) {
      getCompanies({ params: options });
    }
  };

  const renderTags = (tags: Array<{ name: string; color: string }> = []) => {
    return (
      <Flex gap={2} flexWrap="wrap">
        {tags.map((tag, index) => (
          <Tag
            key={index}
            size="sm"
            variant="subtle"
            colorScheme={tag.color || "gray"}
          >
            {tag.name}
          </Tag>
        ))}
      </Flex>
    );
  };

  const renderAccelerator = (accelerator: any) => {
    if (Array.isArray(accelerator)) {
      return accelerator.map(acc => acc.slug).join(", ");
    }
    return accelerator?.slug || "";
  };

  return (
    <Tr
      key={item?.id}
      data-testid="company-row"
      data-row-id={item?.id}
      h={"32px"}
      role="group"
      bg={isSelected ? "lightBlue2" : "transparent"}
      _hover={{ bg: "lightBlue2" }}
      sx={{
        td: {
          borderBottom: "none !important",
          borderColor: "transparent !important",
          backgroundColor: "transparent !important"
        }
      }}
    >
      <CompanyNameRow item={item} isSelected={isSelected} />
      <TdWrapper id="stage" isLast={false} idx={1}>
        <RowText text={item?.stage?.toLowerCase?.().includes("unknown") ? "" : (item?.stage || "")} idx={1} />
      </TdWrapper>
      <TdWrapper id="accelerator" isLast={false} idx={2}>
        {/* Use the AcceleratorListView component to display accelerator information */}
        <AcceleratorListView 
          item={item} 
          maxWidth={columnsWidth && columnsWidth[2] ? columnsWidth[2] : "auto"}
        />
      </TdWrapper>
      <TdWrapper id="yearFounded" isLast={false} idx={3}>
        <RowText text={item?.yearFounded || ""} idx={3} />
      </TdWrapper>
      <TdWrapper id="customerType" isLast={false} idx={4}>
        <ColoredTags
          data={item?.customerType || []} // Provide empty array as fallback for type safety
          handler={onFilteredByTag}
          filterKey={"Customer Type(s)"}
          idx={4}
        />
      </TdWrapper>
      <TdWrapper id="revenueType" isLast={false} idx={5}>
        <ColoredTags
          data={item?.revenueType || []} // Provide empty array as fallback for type safety
          handler={onFilteredByTag}
          filterKey={"Revenue Type(s)"}
          idx={5}
        />
      </TdWrapper>
      <TdWrapper id="tags" isLast={false} idx={6}>
        <ColoredTags
          data={item?.tags || []} // Provide empty array as fallback for type safety
          handler={onFilteredByTag}
          filterKey={"Sector / Industry Tags"}
          idx={6}
        />
      </TdWrapper>
      <TdWrapper id="summary" isLast={false} idx={7}>
        <RowText
          text={item?.summary || ""} // Provide empty string as fallback for type safety
          idx={7}
          style={{
            maxW: columnsWidth && columnsWidth[7] && columnsWidth[7] > 160 ? `${columnsWidth[7]}px` : "160px",
          }}
        />
      </TdWrapper>
      <TdWrapper id="headQuarters" isLast={false} idx={8}>
        {/* Note: Using 'headQuarters' to match existing API field name, though 'headquarters' would be correct spelling */}
        <ColoredTags
          data={[
            item?.headQuarters?.city || { name: "", color: "" },
            item?.headQuarters?.state || { name: "", color: "" },
            item?.headQuarters?.country || { name: "", color: "" },
          ].filter(item => item.name)} // Filter out empty items
          idx={8}
        />
      </TdWrapper>
      <TdWrapper id="raisedInRound" isLast={false} idx={9}>
        <RowText text={item?.raisedInRound || ""} idx={9} />
      </TdWrapper>
      <TdWrapper id="totalRaised" isLast={false} idx={10}>
        <RowText text={item?.totalRaised || ""} idx={10} />
      </TdWrapper>
      <TdWrapper id="description" isLast={false} idx={11}>
        <RowText
          text={item?.description || ""} // Provide empty string as fallback for type safety
          idx={11}
          style={{
            maxW: columnsWidth && columnsWidth[11] && columnsWidth[11] > 160 ? `${columnsWidth[11]}px` : "160px",
          }}
        />
      </TdWrapper>
      <TdWrapper id="employees" isLast={false} idx={12}>
        {/* 
         * Format employee numbers as comma-separated integers
         * For employee counts, we want to show the exact number with commas
         * rather than abbreviations like "k" or "M"
         * 
         * @see {@link src/utils/formatNumber.ts} - Utility for number formatting
         */}
        <RowText
          text={item?.employees ? 
            // Ensure we're working with a number, round it to an integer, and format with commas
            (typeof item.employees === 'number' || typeof item.employees === 'string' ? 
              Math.round(parseFloat(item.employees.toString())).toLocaleString() : 
              ""
            ) : 
            ""
          }
          idx={12}
        />
      </TdWrapper>
      <TdWrapper id="acceleratorBatch" isLast={false} idx={13}>
        <RowText text={item?.acceleratorBatch?.cohort || ""} idx={13} />
      </TdWrapper>
      <TdWrapper id="mainProducts" isLast={false} idx={14}>
        <ColoredTags
          data={item?.mainProducts || []} // Provide empty array as fallback for type safety
          handler={onFilteredByTag}
          filterKey={"Main product(s)"}
          idx={14}
        />
      </TdWrapper>
      <TdWrapper id="operatingStatus" isLast={false} idx={15}>
        <Flex
          align={"center"}
          justify={"center"}
          px={"8px"}
          py={"6px"}
          borderRadius={"4px"}
          bg={item?.operatingStatus && statusColors[item.operatingStatus as keyof typeof statusColors]?.bg || "transparent"}
        >
          <RowText
            text={item?.operatingStatus || ""}
            idx={15}
            style={{
              color: item?.operatingStatus && statusColors[item.operatingStatus as keyof typeof statusColors]?.color || "inherit",
            }}
          />
        </Flex>
      </TdWrapper>
      <TdWrapper id="modelType" isLast={false} idx={16}>
        <ColoredTags
          data={item?.modelType || []} // Provide empty array as fallback for type safety
          handler={onFilteredByTag}
          filterKey={"Model Type(s)"}
          idx={16}
        />
      </TdWrapper>
      <TdWrapper id="ownershipType" isLast={false} idx={17}>
        <ColoredTags 
          data={item?.ownershipType || []} // Provide empty array as fallback for type safety
          idx={17} 
        />
      </TdWrapper>
      <TdWrapper id="technologyUsed" isLast={false} idx={18}>
        <ColoredTags
          data={item?.technologyUsed || []} // Provide empty array as fallback for type safety
          handler={onFilteredByTag}
          filterKey={"Technology Used / Offered"}
          idx={18}
        />
      </TdWrapper>
      <TdWrapper id="links" isLast={false} idx={19}>
        <Flex
          style={{
            minWidth: "200px", // Set minimum width to accommodate typical number of social icons
            width: columnsWidth && columnsWidth[19] ? `${columnsWidth[19]}px` : "auto",
            maxWidth: columnsWidth && columnsWidth[19] ? `${columnsWidth[19]}px` : "auto"
          }}
          overflow={"hidden"}
          align={"center"}
          justify={"center"} // Center the icons
          gap={"8px"} // Consistent gap between icons
          px={"10px"} // Add padding for better spacing
          data-testid="links-cell"
        >
          <ExternalUrlIcons
            data={item || {}}
            customStyle={{
              flexDir: "row",
              flexWrap: "nowrap", // Prevent wrapping of icons
              justify: "center", // Center the icons
              align: "center",
              gap: "8px"
            }}
            customStyleIcon={{ width: "20px", height: "20px" }}
            type={"links"}
            pageType={"companiesList"}
          />
        </Flex>
      </TdWrapper>
      <TdWrapper id="lastUpdate" isLast={true} idx={20}>
        <RowText text={item?.lastUpdate || ""} idx={20} />
      </TdWrapper>
    </Tr>
  );
});

CompaniesInfoTableRow.displayName = 'CompaniesInfoTableRow';

export default CompaniesInfoTableRow;
